import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { WeekStreak } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { progressWidgetLabel } from "./progressWidgetCatalog";
import { makeProgressViewStyles } from "./progressViewStyles";
import { WidgetTitleRow } from "./InfoPill";
import { useStatOneColumn } from "./useStatOneColumn";
import { ProgressWidgetStatus } from "./ProgressWidgetStatus";

// PG-58: the consistency streak, the current run of weeks with a session and
// the best one. The sentence says it all in words. #388: no bars here, the
// weekly sessions are Rhythm's chart, the same counts drawn twice.
// ⛔ No nudge to «keep the streak alive» (the Don't-list).
export function ProgressWeekStreakCard({
  theme,
  streak,
  summaryError,
}: {
  theme: UiTheme;
  /** null while the sessions load. */
  streak: WeekStreak | null;
  summaryError: string | null;
}) {
  const { t } = useTranslation();
  const styles = makeProgressViewStyles(theme);
  const oneColumn = useStatOneColumn();
  const titleRow = (
    <WidgetTitleRow theme={theme} title={progressWidgetLabel(t, "weekStreak")} />
  );
  if (summaryError !== null || streak === null) {
    return (
      <ProgressWidgetStatus
        theme={theme}
        titleRow={titleRow}
        error={summaryError}
        loading={streak === null}
      />
    );
  }
  if (streak.best === 0) {
    return (
      <View style={styles.rhythm}>
        {titleRow}
        <Text style={styles.cardHelp}>
          {t("mobile.progress.weekStreakEmpty", {
            defaultValue: "No sessions yet.",
          })}
        </Text>
      </View>
    );
  }

  const stats: Array<[string, number]> = [
    [
      t("mobile.progress.weekStreakCurrent", { defaultValue: "Current streak" }),
      streak.current,
    ],
    [
      t("mobile.progress.weekStreakBest", { defaultValue: "Best streak" }),
      streak.best,
    ],
  ];
  const sentence =
    streak.current === 0
      ? t("mobile.progress.weekStreakNone", {
          defaultValue:
            "No session last week and none yet this week. This week is still open.",
        })
      : streak.currentWeekHasSession
        ? t("mobile.progress.weekStreakWithThisWeek", {
            count: streak.current,
            defaultValue:
              "{{count}} weeks in a row with at least one session, this week included.",
          })
        : t("mobile.progress.weekStreakUpToLastWeek", {
            count: streak.current,
            defaultValue:
              "{{count}} weeks in a row with at least one session, up to last week. This week is still open.",
          });

  return (
    // s3 as the sibling cards' caption gap (their cards are gap s3), not
    // the s4 of the title-to-stats step.
    <View style={[styles.rhythm, { gap: theme.spacing.s3 }]}>
      {titleRow}
      <View style={styles.statGrid}>
        {stats.map(([label, n]) => {
          const unit = t("mobile.progress.weekStreakWeeks", {
            count: n,
            defaultValue: "weeks",
          });
          return (
            <View
              key={label}
              style={[styles.stat, oneColumn && styles.statFull]}
              accessible
              accessibilityLabel={t("mobile.progress.weekStreakStatA11y", {
                label,
                count: n,
                defaultValue: "{{label}}: {{count}} weeks",
              })}
            >
              <Text style={styles.statLabel}>{label}</Text>
              <View style={styles.statValueRow}>
                <Text style={styles.statValue}>{n}</Text>
                <Text style={styles.cardHelp}>{unit}</Text>
              </View>
            </View>
          );
        })}
      </View>
      <Text style={styles.cardHelp}>{sentence}</Text>
    </View>
  );
}
